import { useState, useEffect } from "react";
import { Lock } from "lucide-react";
import { Empty } from "@/components/atlas/Shared";
import { NativeSelect } from "@/components/atlas/Fields";
import { useFetch, usePositions } from "@/lib/positions";
import { useLang } from "@/lib/i18n";
import { fmtShort, fmtDate } from "@/lib/format";
import { fmtClock, fmtMinutes } from "@/lib/time";

const STATUS_CLS = { open: "text-indigo-700 border-indigo-200 bg-indigo-50", submitted: "text-amber-700 border-amber-200 bg-amber-50", approved: "text-emerald-700 border-emerald-200 bg-emerald-50" };

export const TsStatus = ({ status, testid }) => {
  const { t } = useLang();
  return <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_CLS[status]}`}>{status === "approved" && <Lock className="w-3 h-3" />}{t(`t.${status}`)}</span>;
};

export default function Hours() {
  const { t, lang } = useLang();
  const { label } = usePositions();
  const { data: periods } = useFetch("/pay-periods");
  const [period, setPeriod] = useState("");
  useEffect(() => {
    if (periods && !period) setPeriod(periods.find((p) => p.current)?.start || periods[0]?.start || "");
  }, [periods, period]);
  const sel = periods?.find((p) => p.start === period);
  const { data } = useFetch(sel ? "/me/timesheets" : null, sel ? { start: sel.start, end: sel.end } : undefined);

  return (
    <div>
      <div className="mb-6 rise">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="page-title">{t("nav.hours")}</h1>
        <p className="text-sm text-neutral-500 mt-3">{t("t.hoursSub")}</p>
      </div>
      <div className="mb-6">
        <NativeSelect testid="hours-period-select" value={period} onChange={setPeriod}>
          {(periods || []).map((p) => <option key={p.start} value={p.start}>{fmtDate(p.start, lang, "d MMM")} – {fmtDate(p.end, lang, "d MMM yyyy")}{p.current ? ` · ${t("t.current")}` : ""}</option>)}
        </NativeSelect>
      </div>
      {data && (
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="card-lux p-5" data-testid="hours-period-total"><div className="eyebrow">{t("t.periodTotal")}</div><div className="mono text-3xl mt-2 text-[#D9B872]">{fmtMinutes(data.total_minutes)}</div></div>
          <div className="card-lux p-5" data-testid="hours-approved-total"><div className="eyebrow">{t("t.approvedTotal")}</div><div className="mono text-3xl mt-2">{fmtMinutes(data.approved_minutes)}</div></div>
        </div>
      )}
      {data && data.items.length === 0 && <Empty text={t("t.none")} testid="hours-empty" />}
      <div className="space-y-3" data-testid="hours-list">
        {(data?.items || []).map((ts) => (
          <div key={ts.id} className="card-lux p-5" data-testid={`hours-item-${ts.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="eyebrow capitalize">{fmtShort(ts.date, lang)}</div>
                <div className="font-medium mt-1 truncate">{ts.event_name}</div>
                <div className="text-xs text-[#D9B872]">{label(ts.position_key)}</div>
              </div>
              <TsStatus status={ts.status} testid={`hours-status-${ts.id}`} />
            </div>
            <div className="grid grid-cols-4 gap-2 mt-4 text-center">
              {[["t.in", fmtClock(ts.clock_in)], ["t.out", fmtClock(ts.clock_out)], ["t.break", `${ts.break_minutes || 0} min`], ["t.total", fmtMinutes(ts.total_minutes)]].map(([k, v]) => (
                <div key={k} className="rounded-lg bg-white/[0.03] border border-white/[0.05] py-2"><div className="text-[9px] uppercase tracking-wider text-neutral-500">{t(k)}</div><div className="mono text-sm mt-0.5">{v}</div></div>
              ))}
            </div>
            {ts.flags?.includes("edited") && <p className="text-[11px] text-neutral-500 mt-3">{t("t.correctedNote")}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
